import type { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseQueryOptions,
  emptyQueryError,
  restrictApi,
  useQuery,
  useMutation,
  UseMutationOptions,
  serialize,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { IQuoteSetting, IQuoteSettingForm } from '~/purchase/types';
import { usePurchaseContext } from '~/purchase/context';
import { PurchaseQueryKeys } from '~/purchase/api';

type Options = {
  get?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<IQuoteSetting>, IErrorApiResponse>
  >;
  update?: UseMutationOptions<
    ISuccessApiResponse<IQuoteSetting>,
    IErrorApiResponse,
    IQuoteSettingForm
  >;
  duplicate?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse,
    { customerIds: string[] }
  >;
  list?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<IQuoteSetting[]>, IErrorApiResponse>
  > & {
    args: { customers: string[] };
  };
};

export const useManageQuoteSetting = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { state } = usePurchaseContext();
  const { tenantCode } = useConfig();
  const idCustomer = state.targetCustomer?.id as string;

  const getQuery = useQuery({
    queryKey: PurchaseQueryKeys.quoteSetting.get(idCustomer),
    queryFn: () =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IQuoteSetting>>(
            `/offers/api/tenants/${tenantCode}/customer/${idCustomer}/quote-settings`,
          )
        : emptyQueryError(),
    gcTime: 0,
    refetchOnWindowFocus: false,
    enabled: Boolean(idCustomer) && options.get?.enabled,
  });

  const updateMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<IQuoteSetting>>(
        `/offers/api/tenants/${tenantCode}/customer/${idCustomer}/quote-settings`,
        { method: 'PUT', body: JSON.stringify(variables) },
      ),
    ...options.update,
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.quoteSetting.get(idCustomer)],
  });

  const duplicateMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<IQuoteSetting>>(
        `/offers/api/tenants/${tenantCode}/customer/${idCustomer}/quote-settings/duplicate`,
        { method: 'POST', body: JSON.stringify(variables) },
      ),
    ...options.duplicate,
  });

  const listQuery = useQuery({
    queryKey: PurchaseQueryKeys.quoteSetting.list(options.list?.args),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IQuoteSetting[]>>(
        `/offers/api/tenants/${tenantCode}/quote-settings?${serialize(options.list?.args ?? {})}`,
      ),
    ...options.list,
    enabled: Boolean(options.list?.enabled),
  });

  return restrictApi(options, {
    get: getQuery,
    update: updateMutation,
    duplicate: duplicateMutation,
    list: listQuery,
  });
};
